// Central analytics wrapper. Every call site goes through this module so
// consent-gating, PII handling, and the dual-write (Clarity + Postgres) are
// enforced in one place. Adding a second sink (a BI tool in Stage 2, or
// PostHog down the line) is a one-file change.
//
// Event taxonomy + list of known events lives in docs/analytics.md.

export type Consent = "granted" | "denied" | null;

declare global {
  interface Window {
    // Populated by bootClarity() in CookieConsent.tsx (and the marketing-side
    // ConsentAndAnalytics.astro) once the user accepts the analytics category.
    // Starts as a queue shim that buffers calls until the real SDK loads.
    clarity?: (action: string, ...args: unknown[]) => void;
    // Loaded unconditionally from index.html / BaseLayout.astro — cookieless,
    // no consent gate. May be undefined if the script is blocked or fails.
    umami?: {
      track: (
        eventOrProps?: string | Record<string, unknown>,
        eventData?: Record<string, unknown>,
      ) => Promise<void> | void;
    };
  }
}

// Module-scoped state. The consent provider pushes into this via
// setConsent(); everything else reads it. Deliberately not a React context:
// track() gets called from non-component code (e.g. fetch error handlers,
// route guards) and we want a single source of truth accessible anywhere.
let currentConsent: Consent = null;
let pendingUserId: string | null = null;
let pendingPageName: string | null = null;

function clarity(): Window["clarity"] {
  return typeof window !== "undefined" ? window.clarity : undefined;
}

export function getConsent(): Consent {
  return currentConsent;
}

// Apply a consent decision. Safe to call repeatedly with the same value.
// When transitioning to "granted", we replay any identify/setPage calls
// that were buffered while consent was pending — the Clarity session has
// started either way, but only now should it carry identity + page tags.
export function setConsent(next: Consent): void {
  if (next === currentConsent) return;
  currentConsent = next;
  if (next === "granted") {
    clarity()?.("consent");
    if (pendingUserId) clarity()?.("identify", pendingUserId);
    if (pendingPageName) clarity()?.("set", "page", pendingPageName);
  }
  // No explicit "revoke" call in Clarity's Consent API v2 — withholding
  // consent simply means future writes don't set cookies. Microsoft's docs
  // confirm the script keeps firing in cookieless mode either way.
}

// Use the user's UUID, never their email. Pseudonymous is enough for
// session filtering in Clarity and is GDPR-cleaner.
export function identify(userId: string): void {
  pendingUserId = userId;
  if (currentConsent === "granted") clarity()?.("identify", userId);
}

// Tag the current "page" for Clarity's page filter. Call from route
// components' mount effects.
export function setPage(name: string): void {
  pendingPageName = name;
  if (currentConsent === "granted") clarity()?.("set", "page", name);
}

// Cap how long `await track(...)` blocks before a redirect. Umami's tracker
// uses fetch() with no keepalive/sendBeacon, so an unawaited send racing page
// unload typically loses the event. Awaiting up to this budget gives the
// request room to flush on a healthy network without punishing slow ones.
const UMAMI_FLUSH_BUDGET_MS = 300;

// Fire a custom event to both Clarity and Umami. Intended for the marketing
// (anonymous) path; the authenticated app should go through
// useAnalytics().track() so the event is also persisted to Postgres for
// Stage-2 BI.
//
// Umami fires regardless of consent — cookieless, no PII. Clarity remains
// gated behind the analytics consent category.
//
// Returns a Promise that resolves once Umami's send has flushed (or after
// UMAMI_FLUSH_BUDGET_MS, whichever is first). Fire-and-forget call sites can
// ignore the promise; sites about to trigger a top-level navigation should
// `await` to keep the event from being killed by page unload.
export async function track(
  event: string,
  props?: Record<string, unknown>,
): Promise<void> {
  const umamiSend =
    typeof window !== "undefined" ? window.umami?.track(event, props) : undefined;
  if (currentConsent === "granted") {
    // Clarity accepts either (name) or (name, key, value) forms. For richer
    // props, flatten to multiple `set` calls — but for now we only need the
    // event name + optional single string value pairs that callers build.
    clarity()?.("event", event);
    if (props) {
      for (const [key, value] of Object.entries(props)) {
        if (value === undefined || value === null) continue;
        clarity()?.("set", key, String(value));
      }
    }
  }
  if (umamiSend) {
    await Promise.race([
      umamiSend,
      new Promise<void>((resolve) =>
        setTimeout(resolve, UMAMI_FLUSH_BUDGET_MS),
      ),
    ]);
  }
}

// Server-side event insert. Fire-and-forget — analytics must never block a
// user action. Errors are swallowed silently (Sentry picks up anything
// unexpected).
export async function postServerEvent(
  authFetch: (path: string, init?: RequestInit) => Promise<Response>,
  event: string,
  props?: Record<string, unknown>,
): Promise<void> {
  if (currentConsent !== "granted") return;
  try {
    await authFetch("/api/events", {
      method: "POST",
      body: JSON.stringify({ event, props: props ?? {} }),
    });
  } catch {
    // Swallow — analytics failures never surface to users.
  }
}

// Persist a consent choice to the server. Called by the consent gate
// component; also exposed so the marketing → authenticated migration path
// can opportunistically PUT a pre-existing cookie-based choice.
export async function putConsent(
  authFetch: (path: string, init?: RequestInit) => Promise<Response>,
  consent: "granted" | "denied",
): Promise<void> {
  try {
    await authFetch("/api/me/consent", {
      method: "PUT",
      body: JSON.stringify({ consent }),
    });
  } catch {
    // Consent persistence failures fall back to the current session's
    // in-memory state; user will be re-asked on next login.
  }
}
